import { obterChaveOrcamentoEmAndamentoPorAba, QUATRO_DIAS_EM_MS } from 'app/redux/store/store'
import { OrcamentoEmAndamento } from 'app/redux/orcamentoEmAndamentoSlice'
import { abaEstaViva } from 'app/utils/abaEstaViva'

declare var window: any
declare var localStorage: any

// 482572 — mesmo princípio de useRecuperarCestaProdutosAnterior.ts, aplicado ao snapshot de
// orçamento em andamento agora que ele também é isolado por aba (ver obterChaveOrcamentoEmAndamentoPorAba
// em app/redux/store/store.ts). Sem esta varredura, a checagem pós-login em _Menu.cshtml
// (verificarOrcamentoEmAndamentoAposLogin) só enxergaria o snapshot da PRÓPRIA aba — e uma aba nova
// (aba_id novo, gerado a cada login sem aba_id prévio em sessionStorage — ver LoginContainer.tsx)
// nunca teria escrito nada na sua própria chave. O snapshot de uma aba fechada anormalmente ficaria
// preso para sempre, sem ninguém para oferecer a recuperação.
const PREFIXO_CHAVE_LOCALSTORAGE = 'persist:orcamentoEmAndamento_'
// Formato usado antes deste refinamento (chave única fixa, compartilhada por todas as abas). Mantido
// só para não perder um snapshot pendente de antes deste deploy — sem dono para checar vivacidade,
// então é sempre tratado como órfão.
const CHAVE_LOCALSTORAGE_LEGADA = 'persist:orcamentoEmAndamento'

// 482572 — abaId exposto para que quem consome o órfão (useRecuperarOrcamentoEmAndamento.ts)
// consiga achar, na MESMA aba morta, uma cesta de produtos pendente (produtosSelecionados_<abaId>)
// e recuperá-la junto — null para o formato legado (chave fixa, sem abaId, ver acima).
export type OrcamentoEmAndamentoOrfao = { chave: string, orcamento: OrcamentoEmAndamento, abaId: string | null }

// redux-persist serializa cada campo do slice individualmente (JSON.stringify por campo, não do
// objeto inteiro) — por isso é necessário um JSON.parse por campo para reconstruir o objeto.
function lerOrcamentoEmAndamentoDeChaveLocalStorage(chaveLocalStorage: string): OrcamentoEmAndamento | null {
    try {
        
        const bruto = localStorage.getItem(chaveLocalStorage)
        if (!bruto) return null
        const nivel1 = JSON.parse(bruto)

        const orcamento: any = {}
        Object.keys(nivel1).forEach(campo => {
            if (campo === '_persist') return
            try {
                orcamento[campo] = JSON.parse(nivel1[campo])
            } catch {
                orcamento[campo] = nivel1[campo]
            }
        })

        return orcamento as OrcamentoEmAndamento
    } catch {
        return null
    }
}

function chaveAtual(): string {
    return 'persist:' + obterChaveOrcamentoEmAndamentoPorAba()
}

function obterChavesDeOutrasAbas(): string[] {
    const chaves: string[] = []
    const atual = chaveAtual()
    for (let i = 0; i < localStorage.length; i++) {
        const chave = localStorage.key(i)
        if (!chave || chave === atual) continue
        if (chave === CHAVE_LOCALSTORAGE_LEGADA || chave.indexOf(PREFIXO_CHAVE_LOCALSTORAGE) === 0) {
            chaves.push(chave)
        }
    }
    return chaves
}

// 482572 — NÃO exige `Numero` (diferente da versão original): um orçamento NOVO ainda não
// gravado (Numero == 0) com itens de verdade também é recuperável (ver cenário "não gravado" em
// useRecuperarOrcamentoEmAndamento.ts) — só é "vazio"/lixo quem não tem NENHUM item.
function orcamentoEstaVazio(orcamento: OrcamentoEmAndamento | null): boolean {
    return !orcamento || !Array.isArray(orcamento.Itens) || orcamento.Itens.length === 0
}

// 482572 — SalvoEm (Date.now() gravado por app/redux/store/store.ts a cada mudança real, ver
// orcamentoEmAndamentoSlice.ts) ausente (snapshot de antes deste deploy) NÃO conta como expirado —
// só expira quem realmente tem o timestamp e ele já passou de QUATRO_DIAS_EM_MS.
function orcamentoExpirou(orcamento: OrcamentoEmAndamento): boolean {
    return typeof orcamento.SalvoEm === 'number' && (Date.now() - orcamento.SalvoEm) > QUATRO_DIAS_EM_MS
}

// Varre snapshots de outras abas: descarta silenciosamente os vazios/ilegíveis/expirados (lixo —
// aba que concluiu/limpou o orçamento e fechou, escrita corrompida, ou mais de QUATRO_DIAS_EM_MS
// desde o último SalvoEm), pula os que pertencem a uma aba ainda viva (em uso normal em outro
// lugar) e devolve TODOS os órfãos de verdade encontrados (não só o primeiro): pode haver, ao
// mesmo tempo, um órfão de orçamento JÁ GRAVADO (Numero > 0, tratado em _Menu.cshtml) e um órfão de
// orçamento NOVO ainda não gravado (Numero == 0, tratado em useRecuperarOrcamentoEmAndamento.ts) —
// cada consumidor filtra a lista pelo próprio critério (Numero truthy ou não, e — para o não
// gravado — TipoOrcamento/CodigoFilialOrcamento batendo com o orçamento aberto agora) e decide qual
// oferecer. "Uma aba por vez" continua valendo, mas por CONSUMIDOR: cada um processa só o primeiro
// item da lista que bate com o seu critério.
export async function localizarOrcamentoEmAndamentoOrfaoDeOutraAba(): Promise<OrcamentoEmAndamentoOrfao[]> {
    // 1ª passada, SÍNCRONA: lê e descarta o lixo (vazio/ilegível/expirado) sem pagar nenhuma espera
    // de rede/probe por chave.
    const candidatos: OrcamentoEmAndamentoOrfao[] = []

    for (const chave of obterChavesDeOutrasAbas()) {
        const orcamento = lerOrcamentoEmAndamentoDeChaveLocalStorage(chave)

        if (orcamentoEstaVazio(orcamento) || orcamentoExpirou(orcamento as OrcamentoEmAndamento)) {
            localStorage.removeItem(chave)
            continue
        }

        const abaId = chave === CHAVE_LOCALSTORAGE_LEGADA ? null : chave.slice(PREFIXO_CHAVE_LOCALSTORAGE.length)
        candidatos.push({ chave, orcamento: orcamento as OrcamentoEmAndamento, abaId })
    }

    // 2ª passada, EM PARALELO: a checagem de vivacidade pode custar até TIMEOUT_PROBE_ABA_VIVA_MS
    // por aba quando cai no ping do BroadcastChannel (ver abaEstaViva.ts). Em série — como era
    // antes — 5 snapshots antigos travavam ~2s ANTES de o aviso de recuperação aparecer, mesmo
    // sendo checagens totalmente independentes entre si.
    const vivacidade = await Promise.all(
        candidatos.map(candidato => candidato.abaId ? abaEstaViva(candidato.abaId) : Promise.resolve(false))
    )

    return candidatos.filter((_, indice) => !vivacidade[indice])
}

export function removerOrcamentoEmAndamentoOrfao(chave: string) {
    localStorage.removeItem(chave)
}

// Remove o snapshot da PROPRIA aba. Usado quando o usuario decide explicitamente descartar o
// orcamento: ao sair pelo menu (MenuPrincipalContainer::onSair) e ao recusar a oferta de
// recuperacao pos-login (_Menu.cshtml::oferecerRecuperacao). Nos dois casos limpar so o slice
// do Redux nao basta - a chave em localStorage sobrevive e a oferta volta no proximo login.
export function removerOrcamentoEmAndamentoDaAbaAtual() {
    localStorage.removeItem(chaveAtual())
}

// Ponte para _Menu.cshtml (script inline, fora do bundle TS/React) — mesmo padrão já usado por
// window.abrirModalCestaProdutosSemNavegar (ver _BaseInterno.cshtml).
window.localizarOrcamentoEmAndamentoOrfaoDeOutraAba = localizarOrcamentoEmAndamentoOrfaoDeOutraAba
window.removerOrcamentoEmAndamentoOrfao = removerOrcamentoEmAndamentoOrfao
window.removerOrcamentoEmAndamentoDaAbaAtual = removerOrcamentoEmAndamentoDaAbaAtual
